import { observer } from "mobx-react";
import styles from "../Page.scss";
import loadingAnimationData from "~assets/lottie/loading.json";
import loanStyles from "../Loan/Loan.scss";
import React, { useEffect, useRef } from "react";
import { useStores } from "netbank-shared/src/hooks";
import { InputSlider, InputSliderType } from "~views/shared/InputSlider/InputSlider";
import { LoanCalculationCell } from "./LoanCalculationCell";
import { ILoanOverpaymentCalculationPage } from "netbank-shared/src/libs/models/Content/Page";
import { getQueryParam } from "~utils/misc";
import { HtmlContent } from "~views/shared/HtmlContent/HtmlContent";
import { Button, Lottie, RadioButtons } from "~views/shared";
import { AccordionFaqBlock } from "~views/shared/ContentArea/Blocks/Accordions/AccordionFaqBlock";
import { LoanType } from "netbank-shared/src/libs/models/Content/Enums";
import { tx } from "netbank-shared/src/libs/i18n";
import { useLocation, useNavigate } from "react-router-dom";
import { toLocaleString } from "netbank-shared/src/libs/utils/monetary";
import { BackButton } from "./BackButton";

export const LoanOverpaymentCalculationPage = observer(() => {
  const { contentStore, calculationStore, loanStore, uiStore } = useStores();
  const currentPage = contentStore.currentPage as ILoanOverpaymentCalculationPage;
  const navigate = useNavigate();
  const { currency, locale } = uiStore;
  const terminationOfferPopupRef = useRef(null);
  const location = useLocation();
  const renderMonetary = (amount: number) => {
    if (Number.isNaN(amount)) {
      amount = 0;
    }

    return toLocaleString(amount, currency, locale, 0);
  };

  const runTerminationOffer = () => {
    uiStore.setModal({
      content: currentPage.loanTerminationModal,
    });
  };
  useEffect(() => {
    const initializeCalculator = async () => {
      const loanId = getQueryParam(location.search, "accountNumber");
      if (!loanId) return;

      loanStore.setCurrentAccount(loanId);
      if (loanStore.currentAccount?.accountType === LoanType.SecuredLoan) {
        navigate(`${tx("routing.lang")}`);
        return;
      }
      await calculationStore.initializeCalculator(loanId);
      calculationStore.runSelfTest(currentPage.alertThreshold);
      loanStore.resetTerminationAmountState();
    };
    if (location !== null) {
      initializeCalculator();
    }
  }, [location]);

  const getReturnPath = () => {
    const currentAccount = loanStore.currentAccount;
    if (!currentAccount) return tx("routing.lang");

    switch (currentAccount.accountType) {
      case LoanType.PrivateLoan:
        return `${tx("routing.lang")}/${tx("routing.privateLoan")}/${currentAccount.accountNumber}`;
      case LoanType.Loan:
        return `${tx("routing.lang")}/${tx("routing.loan")}/${currentAccount.accountNumber}`;
      default:
        return tx("routing.lang");
    }
  };

  useEffect(() => {
    calculationStore.refreshPaymentInformation();
  }, [calculationStore.extraPayment, calculationStore.calculationTarget]);

  return (
    <>
      <div ref={terminationOfferPopupRef} className={loanStyles.terminationOfferContainer}></div>
      <div className={[styles.wrapper, loanStyles.wrapper].join(" ")}>
        <section className={[styles.headerWrapper].join(" ")}>
          <div className={loanStyles.headerContainer}>
            <h1
              style={{
                display: "inline-flex",
              }}
            >
              {currentPage.mainHeader}
            </h1>
            <HtmlContent html={currentPage.mainDescription} />
            {calculationStore.calculationStatus === "loading" && (
              <Lottie
                options={{
                  animationData: loadingAnimationData,
                }}
                height={100}
                width={100}
              />
            )}
          </div>
          {calculationStore.calculationStatus === "error" && (
            <div className={loanStyles.calculationContainer}>
              <div className={loanStyles.paragraphHeader}>{currentPage.errorHeader}</div>
              <HtmlContent html={currentPage.errorBody} />
              <BackButton backText={currentPage.backText} onClick={() => navigate(getReturnPath())} />
            </div>
          )}

          {calculationStore.calculationStatus === "loaded" && (
            <>
              <div className={loanStyles.loanCalculationWrapper}>
                <div className={loanStyles.calculationContainer}>
                  <div className={loanStyles.paragraphHeader}>{currentPage.calculationHeader}</div>
                  <HtmlContent
                    className={loanStyles.overpaymentsText}
                    html={currentPage.calculationDescription}
                  />
                </div>
                <InputSlider
                  min={0}
                  noMargin
                  allowOverflow
                  max={calculationStore.calculationContext?.balance ?? 0}
                  step={1}
                  keyboardStep={100}
                  label={currentPage.amountToBePayed}
                  value={calculationStore.extraPayment}
                  setValue={(amount: number) => calculationStore.setExtraPayment(amount)}
                  sliderType={InputSliderType.Monetary}
                />

                <div className={loanStyles.loanCalculationTargetContainer}>
                  {calculationStore.extraPayment === calculationStore.calculationContext?.balance ? (
                    <div className={loanStyles.infoPopup}>
                      <div className={loanStyles.content}>
                        <h3>{currentPage.loanTerminationPopupHeader}</h3>
                        <HtmlContent html={currentPage.loanTerminationPopupBody} />
                        <div className={loanStyles.buttonBar}>
                          <Button
                            type="secondary"
                            onClick={runTerminationOffer}
                            title={currentPage.loanTerminationPopupAccept}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <RadioButtons
                      label={currentPage.calculationTargetHeader}
                      topText={currentPage.calculationTargetDescription}
                      infoPopover={{
                        popoverTitle: currentPage.calculationTargetHelpHeader,
                        content: currentPage.calculationTargetHelpBody,
                      }}
                      value={calculationStore.calculationTarget}
                      list={[
                        {
                          label: currentPage.calculationTargetMonthlyPaymentLabel,
                          value: "monthlyPayment",
                        },
                        {
                          label: currentPage.calculationTargetInstalmentsCountLabel,
                          value: "numberOfInstallments",
                        },
                      ]}
                      onChange={(item) => {
                        calculationStore.setCalculationTarget(item.value as "monthlyPayment" | "numberOfInstalments");
                      }}
                    />
                  )}
                </div>
              </div>

              <div className={loanStyles.loanCalculationSummaryContainer}>
                <div>
                  <div className={loanStyles.paragraphHeader}>{currentPage.calculationResultHeader}</div>
                  <HtmlContent
                    html={currentPage.calculationResultDescription}
                    className={loanStyles.overpaymentsText}
                  />
                </div>
                <table className={loanStyles.loanCalculationTable}>
                  <thead>
                    <tr>
                      <th className={[loanStyles.tableHeader, loanStyles.leftColumn].join(" ")}>
                        {currentPage.summaryFirstColumnName}
                      </th>
                      <th className={[loanStyles.tableHeader, loanStyles.rightColumn].join(" ")}>
                        {currentPage.summarySecondColumnName}
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <LoanCalculationCell
                        value={renderMonetary(calculationStore.initialLoanPaymentInformation?.monthlyPayment ?? 0)}
                        label={currentPage.monthlyPaymentLabel}
                      />
                      <LoanCalculationCell
                        value={renderMonetary(calculationStore.postCalculationPaymentInformation?.monthlyPayment ?? 0)}
                        label={currentPage.monthlyPaymentLabel}
                      />
                    </tr>

                    <tr>
                      <LoanCalculationCell
                        value={`${calculationStore.initialLoanPaymentInformation?.numberOfInstalments} ${currentPage.instalmentsLabel}`}
                        label={currentPage.numberOfInstallmentsLabel}
                      />
                      <LoanCalculationCell
                        value={`${calculationStore.postCalculationPaymentInformation?.numberOfInstalments} ${currentPage.instalmentsLabel}`}
                        label={currentPage.numberOfInstallmentsLabel}
                      />
                    </tr>

                    <tr>
                      <LoanCalculationCell
                        helpText={currentPage.loanAmountDifferenceHelpBody}
                        helpHeader={currentPage.loanAmountDifferenceHelpHeader}
                        value={renderMonetary(calculationStore.initialLoanPaymentInformation?.loanAmount ?? 0)}
                        label={currentPage.loanAmountLabel}
                      />
                      <LoanCalculationCell
                        value={renderMonetary(calculationStore.postCalculationPaymentInformation?.loanAmount ?? 0)}
                        label={currentPage.loanAmountLabel}
                      />
                    </tr>

                    <tr>
                      <LoanCalculationCell
                        value={`${calculationStore.initialLoanPaymentInformation?.interestRate} %`}
                        label={currentPage.interestRateLabel}
                      />
                      <LoanCalculationCell
                        value={`${calculationStore.postCalculationPaymentInformation?.interestRate} %`}
                        label={currentPage.interestRateLabel}
                      />
                    </tr>

                    <tr>
                      <LoanCalculationCell
                        value={renderMonetary(calculationStore.initialLoanPaymentInformation?.creditCostPerMonth ?? 0)}
                        label={currentPage.creditCostPerMonthLabel}
                      />
                      <LoanCalculationCell
                        value={renderMonetary(
                          calculationStore.postCalculationPaymentInformation?.creditCostPerMonth ?? 0
                        )}
                        label={currentPage.creditCostPerMonthLabel}
                      />
                    </tr>

                    <tr>
                      <LoanCalculationCell
                        helpHeader={currentPage.creditCostHelpHeader}
                        helpText={currentPage.creditCostHelpBody}
                        value={renderMonetary(calculationStore.initialLoanPaymentInformation?.totalCreditCost ?? 0)}
                        label={currentPage.totalCreditCostLabel}
                      />
                      <LoanCalculationCell
                        value={renderMonetary(calculationStore.postCalculationPaymentInformation?.totalCreditCost ?? 0)}
                        label={currentPage.totalCreditCostLabel}
                      />
                    </tr>

                    <tr>
                      <LoanCalculationCell
                        value={`${loanStore.formatMaturityDate(
                          calculationStore.initialLoanPaymentInformation?.dateFullyRepaid
                        )}`}
                        label={currentPage.dateFullyRepaidLabel}
                      />
                      <LoanCalculationCell
                        value={`${loanStore.formatMaturityDate(
                          calculationStore.postCalculationPaymentInformation?.dateFullyRepaid
                        )}`}
                        label={currentPage.dateFullyRepaidLabel}
                      />
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className={loanStyles.loanCalculationBottomSegment}>
                <div>
                  <div className={loanStyles.paragraphHeader}>{currentPage.bottomHeader}</div>
                  <HtmlContent html={currentPage.bottomDescription} className={loanStyles.overpaymentsText} />
                  <BackButton backText={currentPage.backText} onClick={() => navigate(getReturnPath())} />

                  {currentPage.faqBlocks && currentPage.faqBlocks.length > 0 && (
                    <div className={[styles.decisionFaqs, loanStyles.overpaymentsText].join(" ")}>
                      {currentPage.faqBlocks.map((faqBlock, index) => (
                        <div className={loanStyles.faqWrapper} key={index}>
                          <AccordionFaqBlock key={faqBlock.contentLink.guidValue} data={faqBlock} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </>
  );
});
